import React, { Children, cloneElement, useState } from 'react';
import classnames from 'classnames';
import VideoCover from 'react-video-cover';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';

import { DisplayComponent } from 'formlabs-web-components/BaseComponents';
import { Button, Link } from 'formlabs-web-components/Link';
import CenterModal from '../modal/CenterModal';
import useIsMobile from '../../hooks/useIsMobile';

import playIconDark from '../../../../public/play-icon.svg';
import playIconLight from '../../../../public/play-white.svg';

import styles from './VideoAccordion.module.css';

const transitionProps = {
  timeout: 500,
  unmountOnExit: true,
  classNames: {
    enter: styles['fade-enter'],
    enterActive: styles['fade-enter-active'],
    exit: styles['fade-leave'],
    exitActive: styles['fade-leave-active']
  }
};

const VideoAccordionItem = DisplayComponent(
  ({
    active,
    autoPlay,
    classes,
    index,
    loop,
    mute: muted,
    previewThumb,
    previewThumbMobile,
    setActiveItem,
    showTitle,
    showTransition,
    theme,
    title,
    videoSrc,
    videoThumb
  }) => {
    const { t } = useTranslation();
    const [isPlaying, play] = useState(false);
    const isMobile = useIsMobile(640);

    if (!active && isPlaying) {
      play(false);
    }

    const handleClick = () => {
      if (active) {
        play(true);
      } else {
        setActiveItem(index);
      }
    };

    const handleModalClose = () => {
      play(false);
      setActiveItem(null);
    };

    const playButton = (
      <CSSTransition {...transitionProps} in={showTitle}>
        <div styleName={classnames('action', theme)}>
          {title}
          <br />
          {active && !isPlaying ? (
            <Button
              to=""
              onClick={event => {
                event.preventDefault();
              }}
              color="dark"
              styleName="play-button"
            >
              <div styleName="button-fix">
                <img styleName="play" src={playIconLight} alt={title} />{' '}
                {t('Watch the video')}
              </div>
            </Button>
          ) : (
            <img
              styleName="play"
              src={theme === 'light' ? playIconLight : playIconDark}
              alt={title}
            />
          )}
        </div>
      </CSSTransition>
    );

    return (
      <div
        className={classes}
        styleName={classnames('item', {
          active,
          'with-transition': showTransition
        })}
        onClick={handleClick}
        onKeyUp={handleClick}
        role="button"
        tabIndex="0"
      >
        {isMobile ? (
          <>
            <img
              src={previewThumbMobile}
              alt={title}
              styleName={classnames('image', 'active-image')}
            />

            {active ? (
              <CenterModal
                id={videoSrc}
                onClose={handleModalClose}
                noPadding
                width="100vw"
              >
                <VideoCover
                  videoOptions={{
                    src: videoSrc,
                    autoPlay,
                    loop,
                    muted,
                    playsInline: true
                  }}
                />
              </CenterModal>
            ) : null}

            {playButton}
          </>
        ) : active && isPlaying ? (
          <VideoCover
            videoOptions={{
              src: videoSrc,
              autoPlay,
              loop,
              muted,
              playsInline: true,
              preload: 'auto',
              poster: videoThumb
            }}
          />
        ) : (
          <>
            <Helmet>
              <link rel="preload" href={videoSrc} as="video" />
              <link rel="preload" href={videoThumb} as="image" />
            </Helmet>
            <CSSTransition
              {...transitionProps}
              in={!active}
              enter={showTransition}
              exit={showTransition}
            >
              <img
                src={previewThumb}
                alt={title}
                styleName={classnames('image')}
              />
            </CSSTransition>
            <CSSTransition
              {...transitionProps}
              in={active}
              enter={showTransition}
              exit={showTransition}
            >
              <img
                src={videoThumb}
                alt={title}
                styleName={classnames('image')}
              />
            </CSSTransition>

            {playButton}
          </>
        )}
      </div>
    );
  }
);

const VideoAccordion = ({ children, classes }) => {
  const { t } = useTranslation();
  const [activeItem, setActiveItem] = useState(null);
  const handleClick = event => {
    event.preventDefault();
    setActiveItem(null);
  };
  return (
    <>
      <div styleName="back">
        <CSSTransition {...transitionProps} in={activeItem !== null}>
          <Link to="#" onClick={handleClick}>
            <i className="fl-arrow-left" styleName="back-arrow" /> {t('Back')}
          </Link>
        </CSSTransition>
      </div>

      <div className={classes} styleName="wrapper">
        {Children.map(children, (child, index) =>
          cloneElement(child, {
            index,
            setActiveItem,
            active: activeItem === index,
            showTitle: activeItem === index || activeItem === null
          })
        )}
      </div>
    </>
  );
};

export default DisplayComponent(VideoAccordion);
export { VideoAccordionItem };
